import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Lightweight loading placeholders shared by every admin tab.
 *
 * They mirror the shape of what is about to render (a row of stat cards, a
 * table, a card with a few lines) so the page does not jump when data lands,
 * and so a slow endpoint reads as "loading" instead of "empty".
 */

interface CountProps {
  count?: number;
  className?: string;
}

export function StatCardsSkeleton({ count = 5, className }: CountProps) {
  return (
    <div className={cn('grid gap-4 md:grid-cols-2 xl:grid-cols-5', className)} aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index}>
          <CardHeader className="pb-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-2 h-6 w-28" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-3 w-32" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function TableSkeleton({ rows = 5, columns = 5, className }: TableSkeletonProps) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true">
      <div className="flex gap-3 border-b pb-2">
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-3 py-1">
          {Array.from({ length: columns }).map((_, column) => (
            <Skeleton
              key={column}
              className={cn('h-4 flex-1', column === 0 && 'max-w-[40%]')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface CardSkeletonProps {
  lines?: number;
  className?: string;
  /** Height class for a single block (log panes, galleries) instead of lines. */
  block?: string;
}

export function CardSkeleton({ lines = 4, className, block }: CardSkeletonProps) {
  return (
    <Card className={className} aria-busy="true">
      <CardHeader>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-2 h-3 w-64 max-w-full" />
      </CardHeader>
      <CardContent className="space-y-3">
        {block ? (
          <Skeleton className={cn('w-full', block)} />
        ) : (
          Array.from({ length: lines }).map((_, index) => (
            <Skeleton
              key={index}
              className={cn('h-4', index % 3 === 2 ? 'w-2/3' : 'w-full')}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

export function ListSkeleton({ count = 4, className }: CountProps) {
  return (
    <div className={cn('space-y-3', className)} aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="rounded-lg border p-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="mt-2 h-3 w-3/4" />
          <Skeleton className="mt-2 h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}

/** Suspense fallback for a lazily loaded admin tab. */
export function PageSkeleton() {
  return (
    <div className="space-y-6">
      <StatCardsSkeleton />
      <CardSkeleton lines={5} />
      <CardSkeleton lines={3} />
    </div>
  );
}
